import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import ProductForm from './ProductForm';
import ProductList from './ProductList';
import '../../styles/pages/Admin.scss';

const AdminPanel = () => {
  const [products, setProducts] = useState([]);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  const { logout, isAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  
  // Carrega produtos do localStorage quando o componente é montado
  useEffect(() => {
    const storedProducts = localStorage.getItem('lunaProducts');
    if (storedProducts) {
      try {
        setProducts(JSON.parse(storedProducts));
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
        showNotification('Erro ao carregar produtos', 'error');
      }
    }
  }, []);
  
  // Salva produtos no localStorage quando são atualizados
  useEffect(() => {
    if (products.length > 0) {
      localStorage.setItem('lunaProducts', JSON.stringify(products));
    }
  }, [products]);
  
  // Verifica se o usuário está autenticado
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);
  
  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };
  
  const showNotification = (message, type = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Esconde a notificação após 3 segundos
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };
  
  const handleAddProduct = () => {
    setCurrentProduct(null);
    setIsEditingProduct(false);
    setIsAddingProduct(true);
  };
  
  const handleEditProduct = (product) => {
    setCurrentProduct(product);
    setIsAddingProduct(false);
    setIsEditingProduct(true);
  };
  
  const handleDeleteProduct = (productId) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      const updatedProducts = products.filter(p => p.id !== productId);
      setProducts(updatedProducts);
      showNotification('Produto excluído com sucesso');
    }
  };
  
  const handleSaveProduct = (productData) => {
    if (isEditingProduct) {
      // Atualiza um produto existente
      const updatedProducts = products.map(p => 
        p.id === productData.id ? productData : p
      );
      setProducts(updatedProducts);
      showNotification('Produto atualizado com sucesso');
    } else {
      // Adiciona um novo produto
      const newProduct = {
        ...productData,
        id: Date.now(), // Gera um ID único baseado em timestamp
        createdAt: new Date().toISOString()
      };
      setProducts([...products, newProduct]);
      showNotification('Produto adicionado com sucesso');
    }
    
    // Fecha o formulário
    setIsAddingProduct(false);
    setIsEditingProduct(false);
    setCurrentProduct(null);
  };
  
  const handleCancelForm = () => {
    setIsAddingProduct(false);
    setIsEditingProduct(false);
    setCurrentProduct(null);
  };
  
  if (!isAuthenticated) {
    return null; // Não renderiza nada enquanto redireciona
  }

  return (
    <motion.div 
      className="admin-page panel-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="container">
        <motion.div 
          className="admin-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div>
            <h1>Painel Administrativo</h1>
            <p>Gerencie os produtos da loja</p>
          </div>
          <button
            className="admin-button secondary"
            onClick={handleLogout}
          >
            Sair
          </button>
        </motion.div>
        
        {notification.show && (
          <motion.div 
            className={`notification ${notification.type}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {notification.message}
          </motion.div>
        )}
        
        {(isAddingProduct || isEditingProduct) ? (
          <motion.div 
            className="admin-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="section-header">
              <h2>{isEditingProduct ? 'Editar Produto' : 'Adicionar Produto'}</h2>
            </div>
            <ProductForm 
              initialData={currentProduct}
              isEditing={isEditingProduct}
              onSave={handleSaveProduct}
              onCancel={handleCancelForm}
            />
          </motion.div>
        ) : (
          <>
            <motion.div 
              className="admin-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="section-header">
                <h2>Produtos</h2>
                <button 
                  className="admin-button"
                  onClick={handleAddProduct}
                >
                  Adicionar Produto
                </button>
              </div>
              
              <ProductList 
                products={products}
                onEdit={handleEditProduct}
                onDelete={handleDeleteProduct}
              />
            </motion.div>
          </>
        )}
        
        <div className="admin-footer">
          <p>© {new Date().getFullYear()} Luna Pijamas - Painel Administrativo</p>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminPanel; 